/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { useState, useMemo, useEffect } from "react";
import { Modal } from "@mui/material";
import Box from "components/generals/Box";
import DefaultCell from "components/generals/DefaultCell";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import Button from "components/generals/Button";
import useHttp from "hooks/http.hook";
import { TableProps } from "interfaces";
import { BillingEncounterInterface, AppointmentInterface, HeaderEnum } from "medicalsystem-shared";
import { useTranslation } from "react-i18next";
import { CoreAPI_URLs } from "constants/http.const";
import { FormActionType } from "types";
import AppointmentForm from "../forms/AppointmentForm";
import { formatPrice } from "utils/number.utils";
import ButtonDialog from "components/generals/ButtonDialog";

/**
 * Extended properties for the BillingEncounterTable component.
 */
interface ExtendedTableProps extends TableProps<BillingEncounterInterface> {
  onViewAppointment?: (appointmentId: string) => void;
  compact?:boolean;
  readOnly?:boolean;
}

/**
 * Billing encounter table component for displaying and managing billing records.
 * 
 * Features:
 * - Displays billing encounter information in sortable data table
 * - View associated appointment details via modal
 * - Delete billing encounter with confirmation
 * - Formatted price display for billing amounts
 * - Loading state for appointment data fetching
 * - Compact mode option for embedded displays
 * - Read-only mode option to disable actions
 * - Optional callback for custom appointment viewing logic
 * - Automatic list update after delete operations
 * 
 * Table Columns (typical):
 * - Patient name
 * - Provider/doctor name
 * - Service date
 * - CPT codes
 * - Billing amount (formatted currency)
 * - Status
 * - Action buttons
 * 
 * Appointment Integration:
 * - Fetches full appointment data by ID
 * - Displays appointment in modal using AppointmentForm
 * - Handles loading and error states
 * - Closes modal on completion
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based delete operations
 * - Validates appointment access before viewing
 * 
 * @param {ExtendedTableProps} props - Component properties
 * @param {BillingEncounterInterface[]} props.modelList - Array of billing encounters to display
 * @param {Function} props.setModelList - Callback to update billing list after changes
 * @param {Function} props.onViewAppointment - Optional callback when viewing appointment
 * @param {boolean} props.compact - Optional flag for compact display mode
 * @param {boolean} props.readOnly - Optional flag to disable edit/delete actions
 * 
 * @returns {JSX.Element} Billing encounter data table with appointment viewing
 */
const BillingEncounterTable: React.FC<ExtendedTableProps> = ({
  modelList,
  setModelList,
  onViewAppointment,
  compact,
  readOnly,
  ...props
}) => {

  const { t } = useTranslation();
  const [_isLoading, _error, sendRequest] = useHttp();
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentInterface | null>(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [loadingAppointment, setLoadingAppointment] = useState(false);

  const handleViewAppointment = async (appointmentId: string) => {
    if (!appointmentId) return;

    if (onViewAppointment) {
      onViewAppointment(appointmentId);
    }

    setLoadingAppointment(true);
    try {
      await sendRequest(
        CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS,
        (appointmentData: AppointmentInterface) => {
          setSelectedAppointment(appointmentData);
          setShowAppointmentModal(true);
          setLoadingAppointment(false);
        },
        "GET",
        undefined,
        [{ header: HeaderEnum.RESOURCE_ID, value: appointmentId }]
      );
    } catch (error) {
      setLoadingAppointment(false);
    }
  };

  const handleCloseModal = () => {
    setShowAppointmentModal(false);
    setSelectedAppointment(null);
  };

  const deleteHandler = (billingEncounterToDelete: BillingEncounterInterface) => {
    sendRequest<boolean, BillingEncounterInterface>(
      CoreAPI_URLs.BILLING_ENCOUNTER_GENERAL_OPERATIONS,
      onBillingEncounterDeleteHandler,
      "DELETE",
      JSON.stringify(billingEncounterToDelete),
      [{ header: HeaderEnum.RESOURCE_ID, value: billingEncounterToDelete.id }],
      { payload: billingEncounterToDelete }
    );
  };

  const onBillingEncounterDeleteHandler = (
    result: boolean,
    billingEncounterDeleted: BillingEncounterInterface
  ) => {
    const updatedList = modelList.filter((item) => item.id !== billingEncounterDeleted.id);
    setModelList(updatedList);
  };

  const getUITable = (list: BillingEncounterInterface[]) => ({
    columns: [
      {
        Header: t("billingEncounterTable.date"),
        accessor: (row: BillingEncounterInterface) => {
          if (!row.appointment?.arrivalDateTime) return "-";
          const date = new Date(row.appointment.arrivalDateTime);
          return date.toLocaleDateString('es-MX', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit'
          });
        },
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("billingEncounterTable.person"),
        accessor: (row: BillingEncounterInterface) =>
          row.appointment?.patient?.person ? `${row.appointment.patient.person.name} ${row.appointment.patient.person.lastname}` : "-",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("billingEncounterTable.pricingName"),
        accessor: "pricingName",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("billingEncounterTable.pricingDescription"),
        accessor: "pricingDescription",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("billingEncounterTable.price"),
        accessor: "price",
        Cell: ({ value }: any) => <DefaultCell value={formatPrice(value)} />,
      },
      {
        Header: t("billingEncounterTable.discount"),
        accessor: "discount",
        Cell: ({ value }: any) => (
          <DefaultCell value={value != null ? formatPrice(value) : "-"} />
        ),
      },
      {
        Header: t("billingEncounterTable.actions"),
        accessor: (row: BillingEncounterInterface) => row.appointment?.id,
        Cell: ({ value }: any) => (
          <Box display="flex" gap={1}>
            <Button
              color="info"
              size="small"
              onClick={() => handleViewAppointment(value)}
              disabled={!value || loadingAppointment}
            >
              {loadingAppointment ? t("billingEncounterTable.loading") : t("billingEncounterTable.viewAppointment")}
            </Button>
          </Box>
        ),
        disableSorting: true,
      },
    ],
    rows: list,
  });

  const getUITableCompact = (list: BillingEncounterInterface[]) => ({
    columns: [
      {
        Header: t("billingEncounterTable.cpt"),
        accessor: (row: BillingEncounterInterface) => row.pricing?.cpt|| t("billingEncounterTable.noCode"),
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("billingEncounterTable.pricingName"),
        accessor: "pricingName",
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={value} />,
      },
      {
        Header: t("billingEncounterTable.price"),
        accessor: "price",
        sortIndicatorType: "numeric",
        Cell: ({ value }: any) => <DefaultCell value={formatPrice(value)} />,
      },
      {
        Header: t("billingEncounterTable.payer"),
        accessor: (row: BillingEncounterInterface) => row.isClaim?t("billingEncounterTable.insurance"):t("billingEncounterTable.patient"),
        sortIndicatorType: "az",
        Cell: ({ value }: any) => <DefaultCell value={String(value)} />
      },
      !readOnly && {
        Header: t("billingEncounterTable.actions"),
        accessor: "id",
        Cell: ({ value }: any) => (
          <ButtonDialog
            name="cy-delete-billing-encounter"
            onAcceptHandle={() => {
              const billing = list.find((p) => p.id === value);
              if (billing) {
                deleteHandler(billing);
              }
            }}
            buttonText={t("formActionButtons.delete")}
            title={t("billingEncounterTable.deleteConfirmation")}
            description={t("billingEncounterTable.deleteDescription")}
            labelledbyID="BillingDelete"
            describedbyID="BillingDescription"
            />
          ),
        disableSorting: true,
      },
      ].filter(Boolean),
      rows: list,
  });

  const dataTableData = useMemo(() =>  (compact ? getUITableCompact(modelList) : getUITable(modelList)), [modelList, loadingAppointment,readOnly]);

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-end" />
      <DataTable {...props} table={dataTableData} canSearch isLoading={props.isLoading}/>
      <Modal
        open={showAppointmentModal}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          maxWidth: "100%",
          width: "90%",
          alignSelf: "center",
          left: "5%",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: 2,
            padding: 3,
            maxHeight: "90vh",
            overflow: "auto",
            width: "100%",
            maxWidth: "1200px",
          }}
        >
          {selectedAppointment && (
            <AppointmentForm
              model={selectedAppointment}
              action={FormActionType.READ_ONLY}
              isModal={true}
              onCloseAction={handleCloseModal}
              comesFromBilling={true}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default BillingEncounterTable;